import { useState } from "react";
import { Layout } from "@/components/layout/Layout";
import { useAuthStore } from "@/stores/auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/api/client";
import {
  useInvitesQuery,
  useCreateInviteMutation,
  useRevokeInviteMutation,
  type Invite,
} from "@/api/invites";

interface AdminUser {
  id: string;
  username: string;
  role: string;
  email: string | null;
  created_at: string;
}

function useAdminUsersQuery() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => apiFetch<AdminUser[]>("/admin/users"),
  });
}

function useDeleteUserMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ deleted: boolean }>(`/admin/users/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function AdminPage() {
  const user = useAuthStore((s) => s.user);

  if (user?.role !== "admin") {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <p className="text-sm text-danger">Admin access required</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto max-w-4xl space-y-8 p-6">
        <h1 className="text-2xl font-semibold text-ink">
          Site Administration
        </h1>

        <PlatformInvites />
        <UserManagement />
      </div>
    </Layout>
  );
}

function PlatformInvites() {
  const { data: invites } = useInvitesQuery();
  const createInvite = useCreateInviteMutation();
  const revokeInvite = useRevokeInviteMutation();
  const [email, setEmail] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [copied, setCopied] = useState<string | null>(null);

  // Platform-only invites (no team_id)
  const platformInvites = (invites ?? []).filter((i) => !i.team_id);

  function handleCreate() {
    createInvite.mutate({
      type: "platform",
      email: email.trim() || undefined,
      max_uses: maxUses ? Number(maxUses) : undefined,
    });
    setEmail("");
    setMaxUses("");
  }

  function handleCopy(code: string) {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <section className="rounded-xl border border-line bg-surface-2 p-6">
      <h2 className="mb-4 text-lg font-semibold text-ink">
        Platform Invites
      </h2>
      <p className="mb-4 text-xs text-ink-2">
        Create invite codes that let people sign up to the platform (without
        joining a specific team). Use this when REQUIRE_INVITE is enabled.
      </p>

      <div className="mb-4 flex items-end gap-3">
        <div className="flex-1">
          <label className="mb-1 block text-xs font-medium text-ink-3">
            Email (optional)
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com or leave blank"
            className="w-full rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
          />
        </div>
        <div className="w-28">
          <label className="mb-1 block text-xs font-medium text-ink-3">
            Max uses
          </label>
          <input
            type="number"
            min="1"
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
            placeholder="∞"
            className="w-full rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
          />
        </div>
        <button
          type="button"
          onClick={handleCreate}
          disabled={createInvite.isPending}
          className="rounded-lg bg-accent px-4 py-1.5 text-xs font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-40"
        >
          Create
        </button>
      </div>

      {platformInvites.length === 0 ? (
        <p className="text-xs text-ink-3">No platform invites.</p>
      ) : (
        <div className="space-y-2">
          {platformInvites.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center justify-between rounded-lg border border-line bg-surface px-3 py-2"
            >
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleCopy(inv.code)}
                  className="rounded bg-accent-soft px-2 py-1 font-mono text-sm font-medium text-accent transition-colors hover:bg-surface-3"
                >
                  {copied === inv.code ? "Copied!" : inv.code}
                </button>
                <div className="text-[10px] text-ink-3">
                  {inv.email && <span>for: {inv.email} · </span>}
                  {inv.use_count}{inv.max_uses ? `/${inv.max_uses}` : ""} used
                </div>
              </div>
              <button
                type="button"
                onClick={() => revokeInvite.mutate(inv.id)}
                className="text-xs text-danger/60 hover:text-danger"
              >
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function UserManagement() {
  const { data: users, isLoading } = useAdminUsersQuery();
  const deleteUser = useDeleteUserMutation();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const currentUserId = useAuthStore((s) => s.user?.id);

  return (
    <section className="rounded-xl border border-line bg-surface-2 p-6">
      <h2 className="mb-4 text-lg font-semibold text-ink">
        Users ({users?.length ?? 0})
      </h2>

      {isLoading ? (
        <p className="text-xs text-ink-3">Loading...</p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-line">
          <table className="w-full">
            <thead>
              <tr className="border-b border-line bg-surface-3">
                <th className="px-4 py-2 text-left text-xs font-medium text-ink-3">Username</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-ink-3">Email</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-ink-3">Role</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-ink-3">Joined</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-ink-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {(users ?? []).map((u) => (
                <tr key={u.id} className="bg-surface">
                  <td className="px-4 py-3 text-sm text-ink">{u.username}</td>
                  <td className="px-4 py-3 text-xs text-ink-3">{u.email ?? "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] ${
                      u.role === "admin"
                        ? "bg-accent-soft text-accent border border-accent/20"
                        : "bg-surface-3 text-ink-3 border border-line"
                    }`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-ink-3">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {u.id === currentUserId ? (
                      <span className="text-[10px] text-ink-3">you</span>
                    ) : confirmId === u.id ? (
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => { deleteUser.mutate(u.id); setConfirmId(null); }}
                          className="text-[10px] text-danger hover:underline"
                        >
                          Confirm
                        </button>
                        <span className="text-[10px] text-ink-3">/</span>
                        <button
                          onClick={() => setConfirmId(null)}
                          className="text-[10px] text-ink-2 hover:underline"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmId(u.id)}
                        className="text-xs text-danger/60 hover:text-danger"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
